import { clausesForContract } from '../../data/clauses';
import type { Clause, ClauseKind } from '../../data/clauses';
import { contractKindLabel } from '../../data/contracts';
import { getSupplier } from '../../data/suppliers';
import type {
  Contract,
  ContractId,
  ContractKind,
  ContractStatus,
  ProcurementType,
} from '../../data/types';

export const CONTRACT_KINDS: ContractKind[] = [
  'MSA',
  'Supply agreement',
  'SOW',
  'Quality agreement',
  'Enterprise agreement',
  'Catalog pricing agreement',
  'NDA',
];

export const CONTRACT_STATUSES: ContractStatus[] = [
  'active',
  'expiring',
  'in_renewal',
  'draft',
  'expired',
];

export const STATUS_LABEL: Record<ContractStatus, string> = {
  active: 'Active',
  expiring: 'Expiring',
  in_renewal: 'In renewal',
  draft: 'Draft',
  expired: 'Expired',
};

/**
 * An agreement title for use mid-sentence: ordinary words go lower case, but
 * acronyms such as THHN, SOW and IT keep their capitals.
 */
export function titleInSentence(title: string): string {
  return title.replace(/\b[A-Z][a-z]+\b/g, (word) => word.toLowerCase());
}

export type AgreementSort = 'value' | 'end' | 'supplier';

export interface AgreementFilters {
  q: string;
  type: 'all' | ProcurementType;
  kind: 'all' | ContractKind;
  status: 'all' | ContractStatus;
  sort: AgreementSort;
}

export const DEFAULT_FILTERS: AgreementFilters = {
  q: '',
  type: 'all',
  kind: 'all',
  status: 'all',
  sort: 'value',
};

export interface AgreementRow {
  contract: Contract;
  /** 0 for a stand-alone agreement or a parent, 1 for a child under its parent. */
  depth: 0 | 1;
}

/** Acronyms people still type, searchable although the screen spells them out. */
const SEARCH_ALIASES: [plain: string, acronym: string][] = [
  ['master agreement', 'msa'],
  ['statement of work', 'sow'],
  ['service levels', 'sla'],
  ['data protection', 'dpa'],
  ['confidentiality', 'nda'],
];

function matchesQuery(contract: Contract, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return true;
  const supplier = getSupplier(contract.supplierId);
  const haystack = [
    contract.title,
    contract.id,
    contract.kind,
    contractKindLabel(contract.kind),
    supplier.name,
    supplier.shortName,
    ...contract.tags,
  ]
    .join(' ')
    .toLowerCase();
  const aliases = SEARCH_ALIASES.filter(([plain]) => haystack.includes(plain))
    .map(([, acronym]) => acronym)
    .join(' ');
  return needle
    .split(/\s+/)
    .every((word) => `${haystack} ${aliases}`.includes(word));
}

function matchesFilters(
  contract: Contract,
  filters: AgreementFilters,
): boolean {
  if (filters.type !== 'all' && contract.type !== filters.type) return false;
  if (filters.kind !== 'all' && contract.kind !== filters.kind) return false;
  if (filters.status !== 'all' && contract.status !== filters.status) {
    return false;
  }
  return matchesQuery(contract, filters.q);
}

function compareContracts(
  a: Contract,
  b: Contract,
  sort: AgreementSort,
): number {
  if (sort === 'end') return a.endDate.localeCompare(b.endDate);
  if (sort === 'supplier') {
    return getSupplier(a.supplierId).shortName.localeCompare(
      getSupplier(b.supplierId).shortName,
    );
  }
  return b.annualValue - a.annualValue || a.id.localeCompare(b.id);
}

/**
 * Filters and orders agreements for the repository table. Children (SOWs and
 * quality agreements) sit directly under their parent when the parent is also
 * in the result; otherwise they appear as stand-alone rows.
 */
export function buildAgreementRows(
  contracts: Contract[],
  filters: AgreementFilters,
): AgreementRow[] {
  const matched = contracts.filter((contract) =>
    matchesFilters(contract, filters),
  );
  const matchedIds = new Set(matched.map((contract) => contract.id));
  const sorted = [...matched].sort((a, b) =>
    compareContracts(a, b, filters.sort),
  );

  const rows: AgreementRow[] = [];
  for (const contract of sorted) {
    if (contract.parentId !== null && matchedIds.has(contract.parentId)) {
      continue;
    }
    rows.push({ contract, depth: 0 });
    for (const child of sorted) {
      if (child.parentId === contract.id) {
        rows.push({ contract: child, depth: 1 });
      }
    }
  }
  return rows;
}

export function hasActiveFilters(filters: AgreementFilters): boolean {
  return (
    filters.q.trim().length > 0 ||
    filters.type !== 'all' ||
    filters.kind !== 'all' ||
    filters.status !== 'all'
  );
}

// ---------------------------------------------------------------------------
// Key terms shown as clause chips in the repository table.
// ---------------------------------------------------------------------------

/** Order in which clause kinds are picked as a row's key terms. */
const KEY_TERM_PRIORITY: ClauseKind[] = [
  'payment_terms',
  'incoterms',
  'committed_spend',
  'rate_card',
  'not_to_exceed',
  'markup',
  'price_adjustment',
  'pricing',
  'sla',
  'change_notification',
  'auto_renewal',
  'termination',
  'confidentiality',
];

/** Up to `limit` clause ids for a contract, payment terms first. */
export function keyTermClauseIds(contractId: ContractId, limit = 3): string[] {
  const clauses = clausesForContract(contractId);
  const picked: Clause[] = [];
  for (const kind of KEY_TERM_PRIORITY) {
    if (picked.length >= limit) break;
    const match = clauses.find(
      (clause) => clause.kind === kind && !picked.includes(clause),
    );
    if (match) picked.push(match);
  }
  return picked.map((clause) => clause.id);
}
